import {useState} from "react"
import {Row, Col} from "antd"
import debounce from "debounce-promise"
import moment from "moment"
import {BasePopup} from "components/popup"
import {BaseButton} from "components/buttons"
import {KlassDropAsyncPaginate, KlassDropdown} from "components/Select"
import {AcademicPlans} from "types/academicPlans"
import {academicPlansService, academicCourseGradesService, courseScheduleService, termsService} from "services"
import {handleError, isPositiveFloat, toastError, toastSuccess} from "helpers"
import {FormLabel} from "components/Form"
import styles from "./ChangeCourseStatusPopup.module.css"
import {BaseDatePicker} from "components/DateTimePicker"
import {AcademicInstructional} from "types/academicInstructional"
import {useModel} from "hooks"
import {head, isEmpty} from "lodash"
import {useQuery} from "@tanstack/react-query"
import {CourseGradeForm} from "./CourseGradeForm"

type Props = {
    isShow: boolean
    studentProfileId: number
    activeCourseStatus?: {
        termId: number
        courseId: number
        academicPlanCourseId: number
        scheduleId: number
        courseStatus: AcademicPlans.AcademicPlanCourseStatus
    }
    sectionCourse?: AcademicPlans.SectionCourse
    onClose: () => void
    onSuccess: () => void
}

const STATUSES = [
    {id: AcademicPlans.AcademicPlanCourseStatus.Registered, name: "Registered"},
    {id: AcademicPlans.AcademicPlanCourseStatus.Completed, name: "Completed"},
    {id: AcademicPlans.AcademicPlanCourseStatus.Failed, name: "Failed"},
    {id: AcademicPlans.AcademicPlanCourseStatus.Dropped, name: "Dropped"}
]

export function ChangeCourseStatusPopup(props: Props) {
    const {isShow, studentProfileId, activeCourseStatus, sectionCourse, onClose, onSuccess} = props
    const [isSaving, setIsSaving] = useState(false)
    const [info, setInfo] = useState({
        totalGrade: null,
        maxTotalGrade: null,
        term: sectionCourse?.termId ? {id: sectionCourse.termId, name: sectionCourse.termName} : null,
        schedule: sectionCourse?.registeredScheduleId
            ? {id: sectionCourse.registeredScheduleId, schedule_suffix: sectionCourse.scheduleName}
            : null,
        isEnterGrade: false,
        weightedAvgValue: null,
        gpaValue: null,
        alphaValue: null,
        totalValue: null,
        maxTotalValue: null,
        status:
            STATUSES.find((item) =>
                sectionCourse?.status ? item.id === sectionCourse.status : item.id === activeCourseStatus?.courseStatus
            ) || null,
        academicStudentStatsId: null
    })
    const [dropDate, setDropDate] = useState(null)
    const [schedules, setSchedules] = useState([])
    const model = useModel()
    const isInAcademicPlan = !isEmpty(sectionCourse)
    const courseId = isInAcademicPlan ? sectionCourse.courseId : activeCourseStatus.courseId
    const scheduleId = isInAcademicPlan ? info.schedule?.id : activeCourseStatus.scheduleId
    const termId = isInAcademicPlan ? sectionCourse.termId : activeCourseStatus.termId
    const gradeStatuses = [
        AcademicPlans.AcademicPlanCourseStatus.Completed,
        AcademicPlans.AcademicPlanCourseStatus.Failed
    ]
    const isShowGradeInfo = gradeStatuses.includes(info.status?.id)

    const {isLoading} = useQuery({
        queryKey: ["course-grade-student", studentProfileId, courseId, scheduleId, termId],
        queryFn: async () => {
            const {courseGrades} = await academicCourseGradesService.getCourseGradeStudents({
                filter: {
                    studentProfileIds: [studentProfileId],
                    courseIds: [courseId],
                    scheduleId: scheduleId,
                    termIds: [termId]
                }
            })
            return head(courseGrades)
        },
        onSuccess: (courseGrade: any) => {
            setInfo({
                ...info,
                totalValue: courseGrade.total,
                weightedAvgValue: courseGrade.weightedAvg,
                alphaValue: courseGrade.alpha,
                gpaValue: courseGrade.gpa,
                academicStudentStatsId: courseGrade.academicStudentStatsId || undefined
            })
        },
        onError: (err) => {
            handleError(err)
        },
        enabled: !!studentProfileId && !!courseId && !!scheduleId && !!termId
    })

    const validateData = () => {
        if (!info.status) {
            toastError("Please select the status")
            return false
        }
        return true
    }

    const getPayload = () => {
        let changeStatusPayload: any = {studentProfileId, status: info.status.id, dropDate: dropDate || undefined}
        let gradePayload: any = {
            weightedAvg: info.weightedAvgValue ? parseFloat(info.weightedAvgValue) : 0,
            isPassed: info.status.id === AcademicPlans.AcademicPlanCourseStatus.Completed,
            passFail:
                info.status.id === AcademicPlans.AcademicPlanCourseStatus.Completed
                    ? AcademicInstructional.PassFailStatus.Pass
                    : AcademicInstructional.PassFailStatus.Fail,
            gpa: info.gpaValue ? parseFloat(info.gpaValue) : 0,
            alphabetical: info.alphaValue || undefined,
            totalGrade: info.totalValue ? parseFloat(info.totalValue) : 0,
            maxTotalGrade: info.maxTotalValue ? parseFloat(info.maxTotalValue) : 0,
            isVerified: true,
            courseId,
            scheduleId,
            studentProfileId
        }
        if (isInAcademicPlan) {
            changeStatusPayload = {
                ...changeStatusPayload,
                termId: info.term?.id,
                academicPlanCourseId: sectionCourse.academicPlanCourseId,
                scheduleId: info.schedule?.id
            }
            gradePayload = {
                ...gradePayload,
                academicStudentStatsId: info.academicStudentStatsId,
                termId: info.term?.id
            }
            return {changeStatusPayload, gradePayload}
        }

        const {termId, academicPlanCourseId} = activeCourseStatus
        changeStatusPayload = {...changeStatusPayload, termId, academicPlanCourseId, scheduleId}
        gradePayload = {...gradePayload, termId}
        return {changeStatusPayload, gradePayload}
    }

    const updateStatus = async () => {
        const isValid = validateData()
        if (!isValid) {
            return
        }
        const {changeStatusPayload, gradePayload} = getPayload()
        try {
            setIsSaving(true)
            await academicPlansService.changeCourseStatus(changeStatusPayload)
            if (!isEmpty(info.status) && gradeStatuses.includes(info.status.id)) {
                await academicCourseGradesService.addManualGrade({
                    listGrades: [gradePayload]
                })
            }
            toastSuccess("Update successfully")
            onSuccess()
        } catch (error) {
            handleError(error)
        } finally {
            setIsSaving(false)
        }
    }

    const updateField = (field, value) => {
        const newInfo = {...info}
        newInfo[field] = value
        setInfo(newInfo)
    }

    const onChangeInfo = (field, value) => {
        switch (field) {
            case "totalValue":
            case "weightedAvgValue":
            case "gpa":
                if (isPositiveFloat(value)) {
                    updateField(field, value)
                } else {
                    toastError(`Invalid value`)
                }
                break
            case "term":
                getSchedules(value?.id)
                updateField(field, value)
                break
            default:
                updateField(field, value)
                break
        }
    }

    const getSchedules = async (termId: number) => {
        if (!termId) {
            setSchedules([])
            return
        }
        try {
            const {data: schedules} = await courseScheduleService.courseScheduleGet({
                filter: {term_id: termId, course_id: sectionCourse.courseId}
            })
            setSchedules(schedules)
        } catch (error) {
            handleError(error)
            setSchedules([])
        }
    }

    const onTermSearchChange = async (search = "", loadedOptions) => {
        try {
            const {data, total} = await termsService.getAllTerms({
                limit: 20,
                offset: loadedOptions.length,
                text: search
            })
            const terms = data.map(({id, name, start_date}) => ({
                id,
                name: `${name} (${moment.utc(start_date).format(model.dateFormat)})`
            }))
            return {
                options: terms,
                hasMore: loadedOptions.length < total
            }
        } catch (error) {
            handleError(error)
            return {options: [], hasMore: false}
        }
    }

    const onTermSearchChangeDebounced = debounce(onTermSearchChange, 300)

    const renderCourseGradeForm = () => {
        if (isEmpty(info.status) || !gradeStatuses.includes(info.status.id) || !isShowGradeInfo) {
            return null
        }
        return <CourseGradeForm info={info} isLoading={isLoading} onChangeInfo={onChangeInfo} />
    }

    return (
        <BasePopup isShow={isShow} onClose={onClose} leftIcon="EDIT_LINE" width="60vw">
            <div className={styles.wrap}>
                <p className={styles.title}>
                    Change Course Status
                    {isInAcademicPlan && ` - ${sectionCourse.courseName}(${sectionCourse.courseCode})`}
                </p>
                <Row gutter={[24, 12]}>
                    <Col span={12}>
                        <div>
                            <FormLabel label="Status" isRequired />
                            <KlassDropdown
                                options={STATUSES}
                                value={info.status}
                                onChange={(newValue) => onChangeInfo("status", newValue)}
                            />
                        </div>
                    </Col>
                    <Col span={12}>
                        {info.status?.id === AcademicPlans.AcademicPlanCourseStatus.Dropped && (
                            <div>
                                <FormLabel label="Drop Date" />
                                <BaseDatePicker value={dropDate} onChange={(newValue) => setDropDate(newValue)} />
                            </div>
                        )}
                    </Col>
                    {Boolean(sectionCourse) && (
                        <>
                            <Col span={12}>
                                <div>
                                    <FormLabel label="Term" />
                                    <KlassDropAsyncPaginate
                                        loadOptions={onTermSearchChangeDebounced}
                                        onChange={(newValue) => onChangeInfo("term", newValue)}
                                        value={info.term}
                                        placeholder="Term"
                                    />
                                </div>
                            </Col>
                            <Col span={12}>
                                <div>
                                    <FormLabel label="Schedule" />
                                    <KlassDropdown
                                        options={schedules}
                                        valueKey="id"
                                        labelKey="schedule_suffix"
                                        value={info.schedule}
                                        onChange={(newValue) => onChangeInfo("schedule", newValue)}
                                    />
                                </div>
                            </Col>
                        </>
                    )}
                </Row>
                {renderCourseGradeForm()}
                <div className={styles.action}>
                    <BaseButton title="Cancel" variant="secondary" loading={isSaving} onClick={onClose} />
                    <BaseButton title="Save" loading={isSaving} onClick={updateStatus} />
                </div>
            </div>
        </BasePopup>
    )
}
